import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Calendar,
  Clock,
  User,
  Phone,
  Smartphone,
  Loader2,
  Search,
  MoreVertical,
  CheckCircle,
  XCircle,
  ArrowRight,
  AlertCircle,
  CalendarDays,
  FileText,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  confirmed: { label: "Confirmed", color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle },
  pending: { label: "Pending", color: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: Clock },
  converted_to_job: { label: "Converted", color: "bg-blue-100 text-blue-700 border-blue-200", icon: FileText },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
  no_show: { label: "No Show", color: "bg-gray-100 text-gray-700 border-gray-200", icon: AlertCircle },
};

export default function Bookings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({});
  const [activeTab, setActiveTab] = useState("today");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchBookings();
    fetchStats();
  }, []);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/bookings`);
      setBookings(res.data.bookings || []);
    } catch (error) {
      console.error("Failed to fetch bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/bookings/stats`);
      setStats(res.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  };

  const handleConvertToJob = async (booking) => {
    if (!window.confirm("Convert this booking to a job? This will create a new job card.")) {
      return;
    }

    setActionLoading(true);
    try {
      const res = await axios.post(`${API}/bookings/${booking.id}/convert-to-job`);
      toast.success(`Job ${res.data.job_number} created!`);
      fetchBookings();
      fetchStats();
      setShowDetailModal(false);
      
      // Navigate to the new job
      if (res.data.job_id) {
        navigate(`/jobs/${res.data.job_id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to convert booking");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async (booking, newStatus) => {
    setActionLoading(true);
    try {
      await axios.put(`${API}/bookings/${booking.id}`, { status: newStatus });
      toast.success(`Booking marked as ${STATUS_CONFIG[newStatus]?.label || newStatus}`);
      fetchBookings();
      fetchStats();
      setShowDetailModal(false);
    } catch (error) {
      toast.error("Failed to update booking status");
    } finally {
      setActionLoading(false);
    }
  };

  // Filter bookings based on tab and filters
  const getFilteredBookings = () => {
    let filtered = [...bookings];
    const today = new Date().toISOString().split("T")[0];

    // Tab filter
    if (activeTab === "today") {
      filtered = filtered.filter((b) => b.appointment_date === today);
    } else if (activeTab === "upcoming") {
      filtered = filtered.filter((b) => b.appointment_date > today && b.status === "confirmed");
    } else if (activeTab === "past") {
      filtered = filtered.filter((b) => b.appointment_date < today);
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter((b) => b.status === statusFilter);
    }

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (b) =>
          b.customer_name?.toLowerCase().includes(query) ||
          b.customer_mobile?.includes(query) ||
          b.booking_number?.toLowerCase().includes(query) ||
          b.device_type?.toLowerCase().includes(query)
      );
    }

    // Sort by date and time
    filtered.sort((a, b) => {
      const dateCompare = a.appointment_date.localeCompare(b.appointment_date);
      if (dateCompare !== 0) return dateCompare;
      return (a.appointment_slot || "").localeCompare(b.appointment_slot || "");
    });

    return filtered;
  };

  const filteredBookings = getFilteredBookings();

  const BookingCard = ({ booking }) => {
    const status = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
    const StatusIcon = status.icon;

    return (
      <Card
        className="cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => {
          setSelectedBooking(booking);
          setShowDetailModal(true);
        }}
        data-testid={`booking-card-${booking.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              {/* Header */}
              <div className="flex items-center gap-2">
                <Badge variant="outline" className={status.color}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {status.label}
                </Badge>
                <span className="text-xs text-muted-foreground font-mono">
                  {booking.booking_number}
                </span>
              </div>

              {/* Customer Info */}
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1 font-medium">
                  <User className="w-4 h-4 text-muted-foreground" />
                  {booking.customer_name}
                </span>
                <span className="flex items-center gap-1 text-muted-foreground">
                  <Phone className="w-3 h-3" />
                  {booking.customer_mobile}
                </span>
              </div>

              {/* Device Info */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Smartphone className="w-4 h-4" />
                <span>
                  {booking.device_type}
                  {booking.brand && ` - ${booking.brand}`}
                  {booking.model && ` ${booking.model}`}
                </span>
              </div>

              {/* Appointment Time */}
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1">
                  <CalendarDays className="w-4 h-4 text-primary" />
                  {formatDate(booking.appointment_date)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4 text-primary" />
                  {booking.appointment_slot}
                </span>
              </div>

              {/* Services */}
              {booking.service_names?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {booking.service_names.slice(0, 3).map((name, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      {name}
                    </Badge>
                  ))}
                  {booking.service_names.length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{booking.service_names.length - 3} more
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {booking.status === "confirmed" && (
                  <>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleConvertToJob(booking);
                      }}
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Convert to Job
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateStatus(booking, "no_show");
                      }}
                    >
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Mark No Show
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleUpdateStatus(booking, "cancelled");
                      }}
                      className="text-red-600"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Cancel Booking
                    </DropdownMenuItem>
                  </>
                )}
                {booking.status === "cancelled" && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUpdateStatus(booking, "confirmed");
                    }}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Restore Booking
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Layout>
      <div className="space-y-6 animate-in" data-testid="bookings-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Bookings</h1>
            <p className="text-muted-foreground">Manage customer appointment bookings</p>
          </div>
          <Button variant="outline" onClick={fetchBookings}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="card-shadow">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-primary">{stats.today || 0}</div>
              <p className="text-sm text-muted-foreground">Today</p>
            </CardContent>
          </Card>
          <Card className="card-shadow">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.confirmed || 0}</div>
              <p className="text-sm text-muted-foreground">Confirmed</p>
            </CardContent>
          </Card>
          <Card className="card-shadow">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-blue-600">{stats.converted || 0}</div>
              <p className="text-sm text-muted-foreground">Converted</p>
            </CardContent>
          </Card>
          <Card className="card-shadow">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-red-600">{stats.cancelled || 0}</div>
              <p className="text-sm text-muted-foreground">Cancelled</p>
            </CardContent>
          </Card>
          <Card className="card-shadow">
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-gray-600">{stats.no_show || 0}</div>
              <p className="text-sm text-muted-foreground">No Show</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, phone, or booking number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="converted_to_job">Converted</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="no_show">No Show</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="today">Today</TabsTrigger>
            <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past">Past</TabsTrigger>
            <TabsTrigger value="all">All</TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">No bookings found</p>
              <p className="text-sm">
                {activeTab === "today"
                  ? "No appointments scheduled for today"
                  : activeTab === "upcoming"
                  ? "No upcoming appointments"
                  : "No bookings match your filters"}
              </p>
            </div>
          ) : (
            <div className="grid gap-4 mt-4">
              {filteredBookings.map((booking) => (
                <BookingCard key={booking.id} booking={booking} />
              ))}
            </div>
          )}
        </Tabs>

        {/* Booking Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Booking Details</DialogTitle>
              <DialogDescription>
                {selectedBooking?.booking_number}
              </DialogDescription>
            </DialogHeader>
            {selectedBooking && (
              <div className="space-y-4 py-4">
                {/* Status */}
                <div className="flex items-center gap-2">
                  <Badge
                    variant="outline"
                    className={STATUS_CONFIG[selectedBooking.status]?.color}
                  >
                    {STATUS_CONFIG[selectedBooking.status]?.label || selectedBooking.status}
                  </Badge>
                  {selectedBooking.job_id && (
                    <Badge variant="outline" className="bg-blue-50">
                      Job Created
                    </Badge>
                  )}
                </div>

                {/* Appointment */}
                <div className="bg-muted rounded-lg p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-primary" />
                    <span className="font-medium">
                      {new Date(selectedBooking.appointment_date).toLocaleDateString("en-US", {
                        weekday: "long",
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-primary" />
                    <span className="font-medium">{selectedBooking.appointment_slot}</span>
                  </div>
                </div>

                {/* Customer */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground">Customer</h4>
                  <div className="grid gap-1">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-muted-foreground" />
                      {selectedBooking.customer_name}
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a
                        href={`tel:${selectedBooking.customer_mobile}`}
                        className="text-primary hover:underline"
                      >
                        {selectedBooking.customer_mobile}
                      </a>
                    </div>
                    {selectedBooking.customer_email && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">@</span>
                        {selectedBooking.customer_email}
                      </div>
                    )}
                  </div>
                </div>

                {/* Device */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground">Device</h4>
                  <div className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-muted-foreground" />
                    <span>
                      {selectedBooking.device_type}
                      {selectedBooking.brand && ` - ${selectedBooking.brand}`}
                      {selectedBooking.model && ` ${selectedBooking.model}`}
                    </span>
                  </div>
                </div>

                {/* Problem */}
                <div className="space-y-2">
                  <h4 className="font-medium text-sm text-muted-foreground">Problem Description</h4>
                  <p className="text-sm bg-muted rounded-lg p-3">
                    {selectedBooking.problem_description}
                  </p>
                </div>

                {/* Services */}
                {selectedBooking.service_names?.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground">Services</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedBooking.service_names.map((name, i) => (
                        <Badge key={i} variant="secondary">
                          {name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Timestamps */}
                <div className="text-xs text-muted-foreground">
                  Created: {new Date(selectedBooking.created_at).toLocaleString()}
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              {selectedBooking?.status === "confirmed" && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus(selectedBooking, "cancelled")}
                    disabled={actionLoading}
                    className="text-red-600"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleConvertToJob(selectedBooking)}
                    disabled={actionLoading}
                    data-testid="convert-to-job-btn"
                  >
                    {actionLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4 mr-2" />
                    )}
                    Convert to Job
                  </Button>
                </>
              )}
              {selectedBooking?.job_id && (
                <Button onClick={() => navigate(`/jobs/${selectedBooking.job_id}`)}>
                  View Job
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
